/**
 * D30 in-app maintenance banner — the incident data contract + pure selection
 * logic. The status data plane (the `syncanix-status-<env>` writer publishes
 * `status.json`; operators author `incidents.json`) is documented in
 * `docs/runbooks/maintenance-window.md`. This mirrors that contract
 * independently — the marketing `/status` page parses the same feed, but the
 * two apps are not coupled (each owns its own typed view of the public JSON).
 *
 * The banner surfaces ONE incident at a time: the most severe currently-relevant
 * one. "Relevant" = not dismissed by this browser, not resolved, and either
 * already active OR announced within the lead window (planned maintenance is
 * posted ≥72h ahead per the runbook, so announcements must render before they
 * begin).
 */

export type IncidentSeverity = 'maintenance' | 'degraded' | 'outage';

/** Operator incident text: a plain string OR a per-locale map (EN fallback). */
export type LocalizedText = string | Readonly<Record<string, string>>;

export interface MaintenanceIncident {
  readonly id: string;
  readonly severity: IncidentSeverity;
  /** ISO-8601. A FUTURE value = announced (not yet begun) maintenance. */
  readonly startedAt: string;
  /** ISO-8601. Present + past = resolved (hidden). Absent/future = ongoing. */
  readonly resolvedAt?: string;
  readonly title: LocalizedText;
  readonly body?: LocalizedText;
}

/** The status feed document — only `incidents` matters to the banner. */
export interface StatusFeed {
  readonly incidents?: readonly MaintenanceIncident[];
}

/** Announced maintenance becomes bannered once it's within this lead window. */
export const ANNOUNCE_LEAD_MS = 14 * 24 * 60 * 60 * 1000;

const SEVERITY_RANK: Record<IncidentSeverity, number> = {
  outage: 3,
  degraded: 2,
  maintenance: 1,
};

function isShowable(
  incident: MaintenanceIncident,
  nowMs: number,
  dismissed: ReadonlySet<string>,
  leadMs: number,
): boolean {
  if (dismissed.has(incident.id)) return false;
  if (!(incident.severity in SEVERITY_RANK)) return false;

  const startedMs = Date.parse(incident.startedAt);
  if (Number.isNaN(startedMs)) return false;

  // resolvedAt present AND in the past → done. Absent or future → still ongoing.
  if (incident.resolvedAt !== undefined) {
    const resolvedMs = Date.parse(incident.resolvedAt);
    if (!Number.isNaN(resolvedMs) && resolvedMs <= nowMs) return false;
  }

  // Active now, or announced within the lead window. Anything further out
  // stays off the banner until it draws closer.
  return startedMs - nowMs <= leadMs;
}

/**
 * Pick the single incident the banner should show: the most severe relevant
 * one, tie-broken by the earliest `startedAt`. Returns `undefined` when nothing
 * qualifies (the banner then renders nothing).
 */
export function selectVisibleIncident(
  incidents: readonly MaintenanceIncident[],
  nowMs: number,
  dismissed: ReadonlySet<string>,
  leadMs: number = ANNOUNCE_LEAD_MS,
): MaintenanceIncident | undefined {
  const showable = incidents.filter((incident) => isShowable(incident, nowMs, dismissed, leadMs));
  if (showable.length === 0) return undefined;
  return [...showable].sort((a, b) => {
    const byRank = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (byRank !== 0) return byRank;
    return Date.parse(a.startedAt) - Date.parse(b.startedAt);
  })[0];
}

/**
 * Resolve operator incident text for a locale: a plain string passes through;
 * a per-locale map prefers the active locale, then EN, then any present value.
 * Mirrors the marketing `/status` page's `localizedIncidentText`.
 */
export function localizeIncidentText(
  text: LocalizedText | undefined,
  locale: string,
): string | undefined {
  if (text === undefined || typeof text === 'string') return text;
  return text[locale] ?? text.en ?? Object.values(text)[0];
}

/** A future `startedAt` = announced (not yet begun) — drives the banner label. */
export function isAnnounced(incident: MaintenanceIncident, nowMs: number): boolean {
  const startedMs = Date.parse(incident.startedAt);
  return !Number.isNaN(startedMs) && startedMs > nowMs;
}
